import { AuthContext, CommonContext } from '@repo-level/core-front';
import { Button } from 'components/buttons';
import { OptionsBlock } from 'components/form/primitives/SelectBlock';
import { ModalsContext, ModalType } from 'contexts/modal';
import { amountWithCurrencySymbol } from 'helpers/currency';
import { getCurrencyCentsAndCode } from 'helpers/wallet';
import { useTheme } from 'hooks/theme';
import { useComponentVisible } from 'hooks/visibility';
import {
  useCurrentOptionWallet,
  useFormatCurrentOptionWallet,
} from 'hooks/wallet';
import { useMedia } from 'modules/common/hooks/useMedia';
import type { FC } from 'react';
import { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ScUserWalletsProps } from './styled';
import { ScUserWallets } from './styled';

interface UserWalletsProps {
  hideDepositButton?: boolean;
  disableSelectionForOneCurrency?: boolean;
  onClickDepositButton?: () => void;
  onClickCurrencySelectButton?: () => void;
}

export const UserWallets: FC<
  UserWalletsProps & Pick<ScUserWalletsProps, 'menuMode' | 'darkSelector'>
> = ({
  hideDepositButton,
  disableSelectionForOneCurrency,
  menuMode,
  onClickDepositButton,
  onClickCurrencySelectButton,
  ...props
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('glossary');
  const { isMobile } = useMedia();

  const { ref, openerRef, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false);

  const { currencies } = useContext(CommonContext);
  const { wallets, changeUserWallet, pendingWallets, pendingUser } =
    useContext(AuthContext);
  const { handleModal } = useContext(ModalsContext);

  const {
    preloaderDotsAnimation: PreloaderDotsAnimation,
    arrowRightSmallIcon: RightSmallIcon,
  } = useTheme();

  const currentOptionWallet = useCurrentOptionWallet();
  const formatedCurrentOptionValue = useFormatCurrentOptionWallet(true);

  const optionsWallets = useMemo(
    () =>
      wallets
        .map((wallet) => {
          const { currency, amount } = getCurrencyCentsAndCode(
            wallet.amountCents,
            wallet.currency,
            currencies
          );

          return {
            name: amountWithCurrencySymbol(currency, amount),
            value: wallet.currency,
          };
        })
        .filter((option) => option.value !== currentOptionWallet.currency),
    [wallets, currencies, currentOptionWallet]
  );

  const loading = useMemo(
    () => pendingWallets || pendingUser,
    [pendingWallets, pendingUser]
  );

  const depositButtonProps = useMemo(
    () => (menuMode ? { xxLarge: true } : { large: true, wide: true }),
    [menuMode]
  );

  const isDisabledSelection = useMemo(
    () => !optionsWallets.length && disableSelectionForOneCurrency,
    [disableSelectionForOneCurrency, optionsWallets.length]
  );

  const handleOpenSelector = useCallback(() => {
    if (isDisabledSelection) return;

    onClickCurrencySelectButton && onClickCurrencySelectButton();

    isMobile
      ? handleModal(ModalType.MyCurrencies, {
          stayInSameWindow: true,
        })()
      : setIsComponentVisible(!isComponentVisible);
  }, [
    handleModal,
    isDisabledSelection,
    isComponentVisible,
    isMobile,
    setIsComponentVisible,
    onClickCurrencySelectButton,
  ]);

  return (
    <ScUserWallets
      isActive={isComponentVisible}
      isDisabledSelection={isDisabledSelection}
      menuMode={menuMode}
      {...props}
    >
      <div className="select-wallet">
        {menuMode && <div className="balance">Balance</div>}
        {loading ? (
          <div className="loader">
            Loading
            <PreloaderDotsAnimation />
          </div>
        ) : (
          <button
            className={`current-wallet ${language}`}
            onClick={handleOpenSelector}
            ref={openerRef}
            type="button"
          >
            <div className="wallet-value">
              <p>{formatedCurrentOptionValue}</p>
              {!isDisabledSelection && (
                <div className="arrow">
                  <RightSmallIcon />
                </div>
              )}
            </div>
          </button>
        )}
        {isComponentVisible && !isMobile && (
          <div className="options">
            <OptionsBlock
              addons={
                hideDepositButton ? null : (
                  <Button
                    onClick={handleModal(ModalType.EditCurrencies)}
                    small
                    type="button"
                    whiteTransparent
                    wide
                  >
                    {t('edit')}
                  </Button>
                )
              }
              hideEmptyText
              onChange={(value) => {
                setIsComponentVisible(!isComponentVisible);
                changeUserWallet(value as string);
              }}
              options={optionsWallets}
              paddingTop={36}
              ref={ref}
            />
          </div>
        )}
      </div>
      {!hideDepositButton && (
        <Button
          className="dep-btn"
          green
          onClick={() => {
            onClickDepositButton && onClickDepositButton();
            handleModal(ModalType.Deposit)();
          }}
          {...depositButtonProps}
        >
          {t('deposit')}
        </Button>
      )}
    </ScUserWallets>
  );
};
